import ENDPOINTS from '@/config/apiEndpoints';
import APP_CONFIG from '@/config/app';
import { TransactionInterface } from '@/interfaces/TransactionInterface';
import { UnderlyingYieldBearingAssetInterface } from '@/interfaces/UnderlyingYieldBearingAssetInterface';
import ApiService, { handleUnauthorized } from '@/services/ApiService';
import { enrichTokensWithBlockchainDisplayName } from '@/services/YieldBearingAssets';
import { generateTokenId } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
import { loadTransactionsStub } from '@/utils/stubLoader';
const preprocessYieldBearingAsset = (yieldBearingAsset: any) => {
  const tokensSupportedForWithdrawal = Array.isArray(
    yieldBearingAsset.tokensSupportedForWithdrawal,
  )
    ? enrichTokensWithBlockchainDisplayName(
        yieldBearingAsset.tokensSupportedForWithdrawal,
      )
    : [];
  const underlyingTokens = Array.isArray(yieldBearingAsset.underlyingTokens)
    ? enrichTokensWithBlockchainDisplayName(yieldBearingAsset.underlyingTokens)
    : [];
  const underlyingYieldBearingAssets = Array.isArray(
    yieldBearingAsset.nativeAssets,
  )
    ? yieldBearingAsset.nativeAssets.map(
        (asset: UnderlyingYieldBearingAssetInterface) => ({
          ...asset,
          blockchainDisplayName:
            APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
              asset.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
            ] || asset.blockchain,
        }),
      )
    : [];
  return {
    ...yieldBearingAsset,
    tokensSupportedForWithdrawal,
    underlyingTokens,
    underlyingYieldBearingAssets,
  };
};
const enrichTokenWithBlockchainDisplayName = (token: any) => {
  if (token && token.blockchain) {
    return {
      ...token,
      blockchainDisplayName:
        APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
          token.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
        ] || token.blockchain,
    };
  }
  return token;
};
const preprocessTransactions = (response: any): TransactionInterface[] => {
  return response.transactions.map((transaction: any) => {
    if (transaction.token) {
      transaction.token = enrichTokenWithBlockchainDisplayName(
        transaction.token,
      );
    }
    if (transaction.inputToken) {
      transaction.inputToken = enrichTokenWithBlockchainDisplayName(
        transaction.inputToken,
      );
    }
    if (transaction.outputToken) {
      transaction.outputToken = enrichTokenWithBlockchainDisplayName(
        transaction.outputToken,
      );
    }
    let type: TransactionInterface['type'];
    if (
      transaction.type === `deposit` &&
      transaction.yieldBearingAsset === undefined
    ) {
      type = 'wallet-deposit';
    } else if (
      transaction.type === `withdraw` &&
      transaction.nativeAssets.length === 0
    ) {
      type = 'wallet-withdrawal';
    } else if (transaction.token && transaction.yieldBearingAsset) {
      type = 'yield-bearing-asset-deposit';
    } else if (transaction.inputToken && transaction.outputToken) {
      type = 'trade';
    } else {
      throw new Error(
        `Unknown transaction type for transaction: ${transaction.hash}`,
      );
    }
    const enrichedYieldBearingAsset = transaction.yieldBearingAsset
      ? preprocessYieldBearingAsset(transaction.yieldBearingAsset)
      : undefined;
    const bridgeFee = transaction.bridgeFee || '0';
    const bridgeFeeInUsd = transaction.bridgeFeeInUsd || '0';
    return {
      id: transaction.id || transaction.hash,
      hash: transaction.hash,
      type,
      createdAt: new Date(transaction.createdAt._seconds * 1000),
      updatedAt: new Date(transaction.updatedAt._seconds * 1000),
      amount: transaction.amount,
      amountInUsd: transaction.amountInUsd,
      networkFee:
        type === 'wallet-deposit'
          ? '0'
          : (
              parseFloat(transaction.gasCost || '0') + parseFloat(bridgeFee)
            ).toString(),
      networkFeeInUsd:
        type === 'wallet-deposit'
          ? '0'
          : (
              parseFloat(transaction.gasCostInUsd || '0') +
              parseFloat(bridgeFeeInUsd)
            ).toString(),
      sliverConvenienceFee:
        type === 'wallet-deposit' ? '0' : transaction.sliverFee,
      sliverConvenienceFeeInUsd:
        type === 'wallet-deposit' ? '0' : transaction.sliverFeeInUsd,
      status: transaction.status,
      ...(transaction.token && { asset: transaction.token }),
      ...(type === 'wallet-withdrawal' &&
        transaction.address && { receivingWalletAddress: transaction.address }),
      ...(type === 'wallet-deposit' &&
        transaction.address && { sendingWalletAddress: transaction.address }),
      ...(type === 'trade' && {
        sellAsset: generateTokenId(
          transaction.inputToken?.nativeAsset || transaction.inputToken,
        ),
      }),
      ...(type === 'trade' && {
        buyAsset: generateTokenId(transaction.outputToken),
      }),
      ...(enrichedYieldBearingAsset && {
        yieldBearingAsset: enrichedYieldBearingAsset,
      }),
    };
  });
};
const TRANSACTION_REQUEST_CACHE = new Map<
  string,
  {
    timestamp: number;
    promise: Promise<{ transactions: TransactionInterface[]; cursor: string }>;
    inProgress: boolean;
  }
>();
const THROTTLE_TIME_MS =
  APP_CONFIG.THROTTLE.TRANSACTIONS_THROTTLE_DURATION_IN_MS;
export const fetchTransactions = async (): Promise<{
  transactions: TransactionInterface[];
  cursor: string;
}> => {
  const cacheKey = `${ENDPOINTS.USER_MULTI_WALLET_TRANSACTIONS}?limit=500`;
  const now = Date.now();
  const cached = TRANSACTION_REQUEST_CACHE.get(cacheKey);
  if (
    cached &&
    (cached.inProgress || now - cached.timestamp <= THROTTLE_TIME_MS)
  ) {
    logInfo(
      'Using cached transaction request',
      'TransactionService.fetchTransactions',
    );
    return cached.promise;
  }
  const promise = (async () => {
    try {
      let response: any = null;
      const shouldUseStub =
        process.env.NODE_ENV === 'development' &&
        APP_CONFIG.STUBS?.USE_TRANSACTIONS_STUB;
      if (shouldUseStub) {
        response = await loadTransactionsStub();
        if (response) {
          logInfo(
            'Using stub data for transactions',
            'TransactionService.fetchTransactions',
          );
        }
      }
      if (!response) {
        logInfo(
          'Fetching transactions from API',
          'TransactionService.fetchTransactions',
        );
        response = await ApiService.get(
          `${ENDPOINTS.USER_MULTI_WALLET_TRANSACTIONS}?limit=500`,
        );
      }
      logInfo(
        'Processed transactions successfully',
        'TransactionService.fetchTransactions',
        response,
      );
      const transactions = preprocessTransactions(response);
      const cursor = response.cursor || '';
      return { transactions, cursor };
    } catch (error: any) {
      if (error.isBetaRestricted) {
        logInfo(
          'Beta restriction encountered for transactions',
          'TransactionService.fetchTransactions',
          error,
        );
        handleUnauthorized();
        return { transactions: [], cursor: '' };
      }
      if (error.response) {
        const { status, data } = error.response;
        logError(
          `API error encountered - HTTP ${status}`,
          'TransactionService.fetchTransactions',
          error,
        );
        error.status = status;
        error.details = data;
        if (
          status === 401 &&
          data?.errorCode === 'UNAUTHORIZED' &&
          data?.errorMessage === 'Not part of beta program'
        ) {
          logInfo(
            'Beta restriction encountered for transactions',
            'TransactionService.fetchTransactions',
            error,
          );
          handleUnauthorized();
          return { transactions: [], cursor: '' };
        }
        throw error;
      }
      logError(error, 'TransactionService.fetchTransactions');
      throw error;
    }
  })();
  TRANSACTION_REQUEST_CACHE.set(cacheKey, {
    timestamp: now,
    promise,
    inProgress: true,
  });
  promise
    .then((result) => {
      const entry = TRANSACTION_REQUEST_CACHE.get(cacheKey);
      if (entry && entry.timestamp === now) {
        TRANSACTION_REQUEST_CACHE.set(cacheKey, {
          ...entry,
          inProgress: false,
        });
      }
      return result;
    })
    .catch((error) => {
      logError(
        'Error in promise chain',
        'TransactionService.fetchTransactions',
        error,
      );
      return { transactions: [], cursor: '' };
    });
  setTimeout(() => {
    const entry = TRANSACTION_REQUEST_CACHE.get(cacheKey);
    if (entry && entry.timestamp === now && !entry.inProgress) {
      TRANSACTION_REQUEST_CACHE.delete(cacheKey);
    }
  }, THROTTLE_TIME_MS);
  return promise;
};
